'use client';

// Catches crashes that happen in the ROOT layout or its providers (Redux, Auth,
// Notifications, Theme…). The per-route error.jsx cannot catch those, which is
// why they surfaced as the bare Next.js "Application error: a client-side
// exception has occurred" screen with no details. This boundary replaces the
// whole document, logs the real error (console.error survives production —
// next.config strips only console.log), and recovers from stale-chunk crashes.

const isChunkLoadError = (error) => {
    const msg = `${error?.name || ''} ${error?.message || ''}`;
    return /ChunkLoadError|Loading chunk [\d]+ failed|Loading CSS chunk|dynamically imported module/i.test(
        msg,
    );
};

// This boundary replaces the whole document, so it renders OUTSIDE ThemeProvider
// — MUI tokens are unavailable and the colours have to be resolved by hand. We
// read the same `settings.mode` the sidebar toggle writes, and fall back to the
// OS preference when storage is unreadable. Every access is guarded: a throw in
// here would surface the bare "Application error" screen this file exists to
// replace.
const resolveIsDark = () => {
    if (typeof window === 'undefined') return false;
    try {
        const stored = JSON.parse(window.localStorage.getItem('settings') || '{}');
        if (stored?.mode === 'dark') return true;
        if (stored?.mode === 'light') return false;
    } catch {
        /* storage blocked or corrupt — fall through to the OS preference */
    }
    try {
        return window.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
    } catch {
        return false;
    }
};

export default function GlobalError({ error, reset }) {
    if (typeof window !== 'undefined') {
        // Everything here is wrapped: this is the TOP-MOST boundary, so if its
        // own render threw (e.g. sessionStorage blocked in private mode) React
        // would fall back to the bare "Application error" — the very screen we
        // are trying to replace.
        try {
            // A stale chunk (old tab open across a deploy) is not a real bug —
            // the new build simply renamed the file. Hard-reload once to fetch
            // it, guarding against a reload loop.
            if (isChunkLoadError(error)) {
                const KEY = '__chunk_reload_at';
                const last = Number(sessionStorage.getItem(KEY) || 0);
                if (Date.now() - last > 10000) {
                    sessionStorage.setItem(KEY, String(Date.now()));
                    window.location.reload();
                }
            }
        } catch {
            /* storage blocked — skip auto-reload, still render the fallback UI */
        }

        try {
            // Keep the real error visible for ops even in production.
            // eslint-disable-next-line no-console
            console.error('[global-error]', {
                name: error?.name,
                message: error?.message,
                digest: error?.digest,
                stack: error?.stack,
            });
        } catch {
            /* no-op */
        }
    }

    const isDark = resolveIsDark();
    const c = isDark
        ? { bg: '#121027', paper: '#1B1834', text: '#F8F7FB', muted: 'rgba(227, 225, 236,0.72)', faint: 'rgba(227, 225, 236,0.4)', border: 'rgba(233, 230, 246,0.18)' }
        : { bg: '#F8F7FB', paper: '#FFFFFF', text: '#1E1B48', muted: '#56537A', faint: '#A6A3BA', border: '#E3E1EC' };

    return (
        <html lang="en">
            <body
                style={{
                    minHeight: '100vh',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'system-ui, sans-serif',
                    background: c.bg,
                    color: c.text,
                }}
            >
                <div style={{ textAlign: 'center', padding: '2rem', maxWidth: 520 }}>
                    <h1 style={{ fontSize: 22, marginBottom: 8 }}>L’application n’a pas pu s’afficher</h1>
                    <p style={{ color: c.muted, marginBottom: 20 }}>
                        Rechargez la page ; si le problème persiste, contactez le support.
                    </p>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                        <button
                            onClick={() => reset()}
                            style={{
                                padding: '10px 18px',
                                borderRadius: 100,
                                border: `1px solid ${c.border}`,
                                background: c.paper,
                                color: c.text,
                                cursor: 'pointer',
                                fontWeight: 600,
                            }}
                        >
                            Réessayer
                        </button>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                padding: '10px 18px',
                                borderRadius: 100,
                                border: 'none',
                                background: '#6C4AF2',
                                color: '#fff',
                                cursor: 'pointer',
                                fontWeight: 700,
                            }}
                        >
                            Recharger
                        </button>
                    </div>
                    {error?.digest ? (
                        <p style={{ marginTop: 18, fontSize: 12, color: c.faint }}>
                            Référence : {error.digest}
                        </p>
                    ) : null}
                </div>
            </body>
        </html>
    );
}
